import { useCallback, useEffect, useState } from 'react';
import { applyUpdate, onUpdateAvailable } from '../lib/pwa';

/**
 * The `beforeinstallprompt` event is not in the standard lib DOM types.
 * Minimal shape we rely on.
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  prompt: () => Promise<void>;
}

const DISMISS_KEY = 'ccc-pwa-install-dismissed';

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function wasDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * PWA install + update state for the UI.
 *
 *  - `canInstall` / `promptInstall()`  → Android/desktop Chromium install flow.
 *  - `dismissInstall()`                → hides the banner permanently (persisted).
 *  - `updateAvailable` / `update()`    → "new version" prompt + apply.
 */
export function usePwa() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(() => wasDismissed());
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      // Stop Chrome's default mini-infobar; we render our own discreet banner.
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      try {
        window.localStorage.setItem(DISMISS_KEY, '1');
      } catch {
        /* ignore */
      }
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    const unsubscribe = onUpdateAvailable(() => setUpdateAvailable(true));

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      unsubscribe();
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (choice.outcome === 'dismissed') {
      // Respect the user's choice; don't nag again.
      dismissInstall();
    }
  }, [deferredPrompt]);

  const dismissInstall = useCallback(() => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
  }, []);

  const update = useCallback(() => {
    applyUpdate();
  }, []);

  const canInstall = !!deferredPrompt && !dismissed && !isStandalone();

  return {
    canInstall,
    promptInstall,
    dismissInstall,
    updateAvailable,
    update,
  };
}
